import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Download,
  FileCode2,
  FileSignature,
  Mail,
  MessageCircle,
  Plus,
  Repeat,
  Trash2,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import api, { API, dateIt, eur, money } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Button } from "@/components/ui/button";

const STATUS_STYLE = {
  bozza: "bg-slate-100 text-slate-700",
  inviato: "bg-blue-50 text-blue-700",
  inviata: "bg-blue-50 text-blue-700",
  accettato: "bg-emerald-50 text-emerald-700",
  "da incassare": "bg-amber-50 text-amber-700",
  pagata: "bg-emerald-50 text-emerald-700",
};

const TYPE_LABEL_KEYS = {
  quote: "docs.quote",
  invoice: "docs.invoice",
  credit_note: "docs.creditNote",
  proforma: "docs.proforma",
  receipt: "docs.receipt",
};

// Le pagine raggruppano i documenti affini: preventivi con pro forma, fatture con note di
// credito e ricevute.
const GROUPS = { quote: "quote,proforma", invoice: "invoice,credit_note,receipt" };

// Scarica il file XML della fattura elettronica; se mancano dati elenca cosa serve.
export const downloadXml = async (doc, t) => {
  try {
    const res = await api.get(`/documents/${doc.id}/xml`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.number}.xml`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("File XML scaricato: caricalo sul portale Agenzia delle Entrate.");
  } catch (err) {
    const body = err.response?.data;
    const detail = body instanceof Blob ? JSON.parse(await body.text()) : body;
    const missing = detail?.detail?.missing;
    toast.error(
      missing?.length
        ? `Per la fattura elettronica mancano: ${missing.join(", ")}`
        : "Non è stato possibile generare il file XML."
    );
  }
};

export const downloadPdf = async (doc) => {
  try {
    const res = await api.get(`/documents/${doc.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${doc.number}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch {
    toast.error("Non è stato possibile scaricare il PDF.");
  }
};

const Documenti = ({ type }) => {
  const { t, ts, lang } = useI18n();
  const [docs, setDocs] = useState([]);
  const [mailDoc, setMailDoc] = useState(null);
  const navigate = useNavigate();
  const isQuote = type === "quote";

  // Somma separata per valuta: sommare valute diverse darebbe un totale falso.
  const totalsByCurrency = Object.entries(
    docs.reduce((acc, d) => {
      const key = d.currency || "EUR";
      acc[key] = (acc[key] || 0) + d.total;
      return acc;
    }, {})
  )
    .map(([currency, sum]) => money(sum, currency))
    .join(" · ") || money(0);

  const load = () =>
    api
      .get("/documents", { params: { type: GROUPS[type] || type } })
      .then(({ data }) => setDocs(data));

  useEffect(() => {
    load();
  }, [type]);

  const convert = async (id) => {
    try {
      const { data } = await api.post(`/documents/${id}/convert`);
      toast.success(`${t("docs.converted")} ${data.number}`);
      navigate("/fatture");
    } catch (e) {
      toast.error(t("rem.failed"));
    }
  };

  const remove = async (id) => {
    await api.delete(`/documents/${id}`);
    toast.success(t("docs.deleted"));
    load();
  };

  const creditNote = async (id) => {
    try {
      const { data } = await api.post(`/documents/${id}/credit-note`);
      toast.success(`${t("docs.creditNote")} ${data.number}`);
      load();
    } catch (e) {
      toast.error(t("rem.failed"));
    }
  };

  const makeRecurring = async (id) => {
    try {
      await api.post("/recurrings", { from_document_id: id, frequency: "monthly" });
      toast.success(t("rec.created"));
      navigate("/ricorrenti");
    } catch {
      toast.error(t("rem.failed"));
    }
  };

  // WhatsApp: link pubblico al PDF + messaggio già scritto, l'invio lo conferma l'utente.
  const sendWhatsapp = async (d) => {
    try {
      const { data } = await api.post(`/documents/${d.id}/share`);
      const text = `${t("wa.message")} ${d.number}: ${data.url}`;
      window.open(
        `https://wa.me/${data.phone || ""}?text=${encodeURIComponent(text)}`,
        "_blank",
        "noopener"
      );
    } catch {
      toast.error(t("rem.failed"));
    }
  };

  return (
    <div className="space-y-5" data-testid={isQuote ? "quotes-page" : "invoices-page"}>
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">
          {docs.length} {isQuote ? t("docs.countQuotes") : t("docs.countInvoices")} ·{" "}
          {t("common.total")}{" "}
          <span className="font-mono-num font-semibold text-slate-900" data-testid="docs-total">
            {totalsByCurrency}
          </span>
        </p>
        <Link to={`/documenti/nuovo?type=${type}`}>
          <Button data-testid="new-document-button" className="gap-2 bg-blue-600 text-white hover:bg-blue-700">
            <Plus className="h-4 w-4" /> {isQuote ? t("docs.newQuote") : t("docs.newInvoice")}
          </Button>
        </Link>
      </div>

      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">{t("common.number")}</th>
              <th className="px-4 py-3">{t("common.client")}</th>
              <th className="px-4 py-3">{t("docs.type")}</th>
              <th className="px-4 py-3">{t("common.date")}</th>
              <th className="px-4 py-3">{t("common.status")}</th>
              <th className="px-4 py-3 text-right">{t("common.total")}</th>
              <th className="px-4 py-3 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody data-testid="documents-table">
            {docs.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-slate-500">
                  {t("docs.empty")}
                </td>
              </tr>
            )}
            {docs.map((d) => (
              <tr key={d.id} className="border-b border-slate-100 transition-colors duration-200 hover:bg-slate-50">
                <td className="font-mono-num px-4 py-3 font-medium">
                  <Link to={`/documenti/${d.id}`} className="hover:text-blue-600" data-testid={`open-doc-${d.number}`}>
                    {d.number}
                  </Link>
                </td>
                <td className="px-4 py-3">{d.client?.name}</td>
                <td className="px-4 py-3 text-xs text-slate-500">
                  {t(TYPE_LABEL_KEYS[d.type] || "docs.invoice")}
                </td>
                <td className="font-mono-num px-4 py-3 text-slate-600">{dateIt(d.issue_date)}</td>
                <td className="px-4 py-3">
                  <span className={`rounded px-2 py-0.5 text-xs font-semibold ${STATUS_STYLE[d.status] || "bg-slate-100 text-slate-700"}`}>
                    {ts(d.status)}
                  </span>
                </td>
                <td className="font-mono-num px-4 py-3 text-right font-semibold">{money(d.total, d.currency)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <button
                      data-testid={`pdf-${d.number}`}
                      onClick={() => downloadPdf(d)}
                      title={t("docs.downloadPdf")}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-slate-900 hover:text-white"
                    >
                      <Download className="h-3.5 w-3.5" />
                    </button>
                    {lang === "it" && ["invoice", "credit_note"].includes(d.type) && (d.currency || "EUR") === "EUR" && (
                      <button
                        data-testid={`xml-${d.number}`}
                        onClick={() => downloadXml(d, t)}
                        title="Fattura elettronica XML (SDI)"
                        className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-emerald-600 hover:text-white"
                      >
                        <FileCode2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`whatsapp-${d.number}`}
                      onClick={() => sendWhatsapp(d)}
                      title={t("wa.send")}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-green-600 hover:text-white"
                    >
                      <MessageCircle className="h-3.5 w-3.5" />
                    </button>
                    {d.type === "invoice" && (
                      <button
                        data-testid={`recurring-${d.number}`}
                        onClick={() => makeRecurring(d.id)}
                        title={t("rec.makeRecurring")}
                        className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-violet-600 hover:text-white"
                      >
                        <Repeat className="h-3.5 w-3.5" />
                      </button>
                    )}
                    {d.type === "invoice" && (
                      <button
                        data-testid={`credit-note-${d.number}`}
                        onClick={() => creditNote(d.id)}
                        title={t("docs.makeCreditNote")}
                        className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-amber-600 hover:text-white"
                      >
                        <Undo2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`email-${d.number}`}
                      onClick={() => setMailDoc(d)}
                      title={t("mail.send")}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-blue-600 hover:text-white"
                    >
                      <Mail className="h-3.5 w-3.5" />
                    </button>
                    {["quote", "proforma"].includes(d.type) && (
                      <button
                        data-testid={`convert-${d.number}`}
                        onClick={() => convert(d.id)}
                        title={t("docs.convert")}
                        className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-indigo-600 hover:text-white"
                      >
                        <FileSignature className="h-3.5 w-3.5" />
                      </button>
                    )}
                    <button
                      data-testid={`delete-${d.number}`}
                      onClick={() => remove(d.id)}
                      className="rounded-md p-2 text-slate-500 transition-colors duration-200 hover:bg-red-600 hover:text-white"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {mailDoc && (
        <SendEmailDialog open={!!mailDoc} doc={mailDoc} onClose={() => setMailDoc(null)} />
      )}
    </div>
  );
};

export default Documenti;
